/**
 * CreateCycleModal Component
 *
 * Modal for creating or editing a cycle.
 * Uses React Hook Form with Zod validation for form validation and error handling.
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { CycleFormSchema, CycleFormData, cycleFormDefaults } from '../../../models/cycle.schemas';
import ErrorMessage from '@/components/shared/ErrorMessage/ErrorMessage';
import { useToast } from '@/hooks/useToast';
import type { CreateCycleModalProps } from '../cycles.types';

export default function CreateCycleModal({
  isOpen,
  onClose,
  onSubmit,
  editingCycle,
}: CreateCycleModalProps) {
  const toast = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CycleFormData>({
    resolver: zodResolver(CycleFormSchema),
    mode: 'onBlur',
    defaultValues: cycleFormDefaults,
  });

  useEffect(() => {
    if (editingCycle) {
      reset({
        name: editingCycle.name,
        description: editingCycle.description || '',
        startDate: editingCycle.startDate.toDate(),
        endDate: editingCycle.endDate.toDate(),
      });
    } else {
      reset(cycleFormDefaults);
    }
  }, [editingCycle, isOpen, reset]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  /**
   * Convert date string or Date to local Date (avoiding timezone issues)
   * Input date="2025-01-15" should result in Jan 15 in local time, not UTC
   */
  const toLocalDate = (date: Date | string): Date => {
    if (date instanceof Date) return date;
    // Parse "YYYY-MM-DD" and create date in local timezone
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const handleFormSubmit = async (data: CycleFormData) => {
    try {
      const submitData = {
        name: data.name,
        description: data.description || '',
        startDate: toLocalDate(data.startDate),
        endDate: toLocalDate(data.endDate),
        ...(editingCycle && editingCycle.createdBy && { createdBy: editingCycle.createdBy }),
      };

      await onSubmit(submitData);
      toast.success(editingCycle ? 'Ciclo atualizado com sucesso!' : 'Ciclo criado com sucesso!');
      reset();
      onClose();
    } catch (error: any) {
      toast.error(`Erro ao ${editingCycle ? 'atualizar' : 'criar'} ciclo: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-montserrat text-2xl font-bold text-gray-900">
            {editingCycle ? 'Editar Ciclo' : 'Criar Novo Ciclo'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate className="space-y-4">
          {/* Name */}
          <div>
            <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
              Nome do Ciclo <span className="text-red-500">*</span>
            </label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  disabled={isSubmitting}
                  className={clsx(
                    'w-full rounded-lg border px-4 py-2 font-montserrat focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200',
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  )}
                  placeholder="Ex: Q1 2025"
                />
              )}
            />
            <ErrorMessage errors={errors} fieldPath="name" />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
              Descrição
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  disabled={isSubmitting}
                  className={clsx(
                    'w-full rounded-lg border px-4 py-2 font-montserrat focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200',
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  )}
                  rows={2}
                  placeholder="Descreva o ciclo..."
                />
              )}
            />
            <ErrorMessage errors={errors} fieldPath="description" />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
                Data de Início <span className="text-red-500">*</span>
              </label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="date"
                    disabled={isSubmitting}
                    value={field.value instanceof Date ? field.value.toISOString().split('T')[0] : ''}
                    onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : '')}
                    className={clsx(
                      'w-full rounded-lg border px-4 py-2 font-montserrat focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200',
                      errors.startDate ? 'border-red-500' : 'border-gray-300'
                    )}
                  />
                )}
              />
              <ErrorMessage errors={errors} fieldPath="startDate" />
            </div>

            <div>
              <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
                Data de Término <span className="text-red-500">*</span>
              </label>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="date"
                    disabled={isSubmitting}
                    value={field.value instanceof Date ? field.value.toISOString().split('T')[0] : ''}
                    onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : '')}
                    className={clsx(
                      'w-full rounded-lg border px-4 py-2 font-montserrat focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200',
                      errors.endDate ? 'border-red-500' : 'border-gray-300'
                    )}
                  />
                )}
              />
              <ErrorMessage errors={errors} fieldPath="endDate" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border border-gray-300 px-6 py-2 font-montserrat font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-blue-600 px-6 py-2 font-montserrat font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Salvando...' : editingCycle ? 'Salvar Alterações' : 'Criar Ciclo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
